import React, { createContext, useContext, useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { socketService } from '../services/socketService';
import { useAuthStore } from '../store/authStore';
import { useChatStore, transformBackendOnlineUser } from '../store/chatStore';
import { useGroupStore } from '../store/groupStore';
import { useMessageStore } from '../store/messageStore';
import { toast } from '../components/ui/Toaster';
import type { GroupMessage, User } from '../types';
import type { SendMessageData } from '../types/chatTypes';
import type { ChatMessage } from '../store/chatStore';

// Transform a server-side GroupMessage (from a `new-message` broadcast or a
// `send-message` ack) into the store's ChatMessage shape.
const groupMessageToChatMessage = (message: GroupMessage): ChatMessage => ({
  id: message.id.toString(),
  content: message.content,
  type: 'text' as const,
  messageType: message.messageType || 'TEXT',
  gifUrl: message.gifUrl || null,
  gifId: message.gifId || null,
  groupId: message.groupId.toString(),
  userId: message.userId.toString(),
  username: message.user.username,
  userProfilePhoto: message.user.profilePhoto,
  userSuspendedAt: message.user.suspendedAt,
  createdAt: message.createdAt,
  status: 'sent' as const,
});

interface SocketContextType {
  isConnected: boolean;
  isConnecting: boolean;
  connect: () => Promise<void>;
  disconnect: () => void;
  joinGroup: (groupId: number) => void;
  leaveGroup: (groupId: number) => void;
  sendMessage: (messageData: SendMessageData) => Promise<ChatMessage | null>;
  startTyping: (groupId: number) => void;
  stopTyping: (groupId: number) => void;
}

const SocketContext = createContext<SocketContextType | null>(null);

export const useSocket = () => {
  const context = useContext(SocketContext);
  if (!context) {
    throw new Error('useSocket must be used within a SocketProvider');
  }
  return context;
};

interface SocketProviderProps {
  children: React.ReactNode;
}

export const SocketProvider: React.FC<SocketProviderProps> = ({ children }) => {
  const [isConnected, setIsConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [lastTokenUsed, setLastTokenUsed] = useState<string | null>(null);
  // Reconnect attempt counter kept in a ref: `connect` is recreated each render
  // and re-invoked from setTimeout, so a state value would be read stale inside
  // the retry chain and the exponential backoff would never scale. A ref is
  // stable across renders and read/written synchronously.
  const retryCountRef = useRef(0);
  const maxRetries = 3;
  // Subscribe with SELECTORS, never by destructuring the whole store. A
  // no-selector subscription re-renders this provider on every store set()
  // (every chat message, every read-state update). Combined with the context
  // functions getting a new identity per render, that fed an infinite loop:
  // GroupChatPage's join-effect (deps include joinGroup) runs -> writes to the
  // chat store (setCurrentGroup) -> provider re-renders -> new joinGroup
  // identity -> effect re-runs -> "Maximum update depth exceeded".
  const user = useAuthStore((s) => s.user);
  const token = useAuthStore((s) => s.token);
  const accessToken = useAuthStore((s) => s.accessToken);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const setConnectionStatus = useChatStore((s) => s.setConnectionStatus);
  const setReconnecting = useChatStore((s) => s.setReconnecting);
  const addChatMessage = useChatStore((s) => s.addMessage);
  const addMessage = useMessageStore((s) => s.addMessage);
  const checkUnreadMessages = useMessageStore((s) => s.checkUnreadMessages);

  // Mirror the socket's connection lifecycle into React state. Registered
  // once; the callbacks read live state via getState() so they never go stale.
  // These fire for BOTH the initial connect and every automatic reconnect,
  // which is what fixes the "valselijk Verbonden" bug after a network dip.
  useEffect(() => {
    socketService.setConnectionListeners({
      onConnect: () => {
        setIsConnected(true);
        setConnectionStatus(true, null);
        setReconnecting(false);
      },
      onDisconnect: () => {
        setIsConnected(false);
        setConnectionStatus(false, null);
        // If socket.io is going to auto-reconnect, surface a "reconnecting"
        // state; otherwise the auto-connect effect will establish a new socket.
        setReconnecting(socketService.isActive());
      },
      onReconnect: () => {
        // Backend auto-rejoins the user's group rooms on (re)connect, but we
        // re-emit join for the active group to refresh server-side read state,
        // then backfill any messages missed during the outage (BUG 2).
        const { currentGroupId, refetchRecentMessages } = useChatStore.getState();
        if (currentGroupId) {
          try {
            socketService.joinGroup(parseInt(currentGroupId));
          } catch (err) {
            console.error('Failed to rejoin group after reconnect:', err);
          }
          refetchRecentMessages(currentGroupId);
        }
      },
    });
  }, [setConnectionStatus, setReconnecting]);

  // Auto-connect when user is authenticated or token changes
  useEffect(() => {
    const actualToken = accessToken || token;
    
    // Prevent multiple simultaneous connection attempts
    if (isConnecting) {
      return;
    }
    
    // If token changed and we were connected, reconnect with new token
    if (actualToken && actualToken !== lastTokenUsed && isConnected) {
      setLastTokenUsed(actualToken);
      disconnect();
      // Longer delay to ensure clean disconnection before reconnect
      setTimeout(() => {
        if (!isConnecting) { // Double-check to prevent race condition
          connect();
        }
      }, 500);
      return;
    }
    
    // Connect when authenticated. An in-memory token is optional: after a
    // page reload there is none (tokens live in httpOnly cookies, F1) and the
    // socket handshake then authenticates via the cookie (withCredentials).
    // Guard with isActive(): while socket.io is auto-reconnecting an existing
    // socket, `isConnected` is false but we must NOT spawn a second socket.
    if (isAuthenticated && user && !isConnected && !isConnecting && !socketService.isActive()) {
      setLastTokenUsed(actualToken);
      // Small delay to ensure auth state is fully settled
      setTimeout(() => {
        if (!isConnected && !isConnecting && !socketService.isActive()) { // Double-check conditions
          connect();
        }
      }, 100);
    } else if (!isAuthenticated && isConnected) {
      disconnect();
    }
  }, [isAuthenticated, token, accessToken, user, isConnected, isConnecting]);

  // Set up event listeners
  useEffect(() => {
    if (!isConnected) return;

    const handleNewMessage = (message: GroupMessage) => {
      // Convert GroupMessage to ChatMessage format and add to chat store
      const chatMessage = groupMessageToChatMessage(message);

      addChatMessage(chatMessage.groupId, chatMessage);

      // Unread bookkeeping: own messages don't count; messages for the
      // currently open chat are marked read on the server right away,
      // anything else bumps the badge on the group card
      const isOwnMessage = user?.id?.toString() === chatMessage.userId;
      if (!isOwnMessage) {
        const { currentGroupId } = useChatStore.getState();
        if (currentGroupId === chatMessage.groupId) {
          socketService.markGroupRead(message.groupId);
        } else {
          useGroupStore.getState().incrementGroupUnread(message.groupId);
        }
      }
    };

    const handleUserJoined = (data: { groupId: number; user: User }) => {
      toast.success('Groep update', `${data.user.username} is de groep gejoind`);
    };

    const handleUserLeft = (data: { groupId: number; user: User }) => {
      toast.info('Groep update', `${data.user.username} heeft de groep verlaten`);
    };

    const handleMemberAccepted = (data: { groupId: number; user: User }) => {
      if (data.user.id === user?.id) {
        toast.success('Welkom!', 'Je bent geaccepteerd in de groep');
      } else {
        toast.success('Groep update', `${data.user.username} is geaccepteerd in de groep`);
      }
    };

    const handleMemberRejected = (data: { groupId: number; userId: number }) => {
      if (data.userId === user?.id) {
        toast.error('Groep update', 'Je join verzoek is afgewezen');
      }
    };

    const handleError = (error: { message: string; code?: string }) => {
      console.error('Socket error:', error);
      toast.error('Chat fout', error.message);
    };

    // Direct message handlers
    const handleNewDirectMessage = (message: any) => {
      // Own messages arrive here too (sent from another tab/device); file
      // them under the receiver so every tab shows the full conversation.
      // The tab that sent the message never receives this event — the
      // server excludes the sending socket.
      const currentUserIdStr = user?.id?.toString();
      const isOwnMessage = !!currentUserIdStr && message.senderId?.toString() === currentUserIdStr;

      const otherUserId = isOwnMessage ? message.receiverId : message.senderId;

      // addMessage already handles unread state management, no need for additional API call
      addMessage(otherUserId, message);
    };

    const handleConversationRead = (_data: { userId: number }) => {
      checkUnreadMessages();
    };

    // Server broadcasts the full online-users list of a group on every
    // join/leave/connect/disconnect; mirror it into the chat store so the
    // members panel shows live online status.
    const handleOnlineUsers = (data: { groupId: string; users: any[] }) => {
      useChatStore.getState().setOnlineUsers(
        data.groupId.toString(),
        (data.users || []).map(transformBackendOnlineUser)
      );
    };

    // Add listeners
    socketService.onNewMessage(handleNewMessage);
    socketService.onUserJoined(handleUserJoined);
    socketService.onUserLeft(handleUserLeft);
    socketService.onMemberAccepted(handleMemberAccepted);
    socketService.onMemberRejected(handleMemberRejected);
    socketService.onError(handleError);
    socketService.onOnlineUsers(handleOnlineUsers);

    // Add direct message listeners
    socketService.onDirectMessageReceived(handleNewDirectMessage);
    socketService.onConversationRead(handleConversationRead);

    // Cleanup
    return () => {
      socketService.offNewMessage(handleNewMessage);
      socketService.offUserJoined(handleUserJoined);
      socketService.offUserLeft(handleUserLeft);
      socketService.offMemberAccepted(handleMemberAccepted);
      socketService.offMemberRejected(handleMemberRejected);
      socketService.offError(handleError);
      socketService.offOnlineUsers(handleOnlineUsers);

      // Cleanup direct message listeners
      socketService.offDirectMessageReceived(handleNewDirectMessage);
      socketService.offConversationRead(handleConversationRead);
    };
  }, [isConnected, user?.id, addChatMessage, addMessage, checkUnreadMessages]);

  const connect = useCallback(async () => {
    const actualToken = accessToken || token;
    
    if (!isAuthenticated) {
      return;
    }
    
    if (!user) {
      return;
    }
    
    if (isConnecting) {
      return;
    }
    
    if (isConnected) {
      return;
    }


    try {
      setIsConnecting(true);
      setReconnecting(true);
      // Pass a token *getter* (not a captured string) so automatic reconnects
      // re-read the freshest token from the auth store on each handshake.
      await socketService.connect(
        () => useAuthStore.getState().accessToken || useAuthStore.getState().token || actualToken || ''
      );
      setIsConnected(true);
      setConnectionStatus(true, null); // Update chat store
      retryCountRef.current = 0; // Reset retry count on successful connection
      setIsConnecting(false);
      setReconnecting(false);
    } catch (error: any) {
      console.error('🔌 SOCKET DEBUG: Connection failed:', error);

      const isAuthError =
        error.message?.includes('expired') ||
        error.message?.includes('invalid') ||
        error.message?.includes('Authentication');

      // Handle retry logic with a correctly-scaling exponential backoff.
      if (retryCountRef.current < maxRetries && !isAuthError) {
        const attempt = retryCountRef.current;
        const delay = Math.pow(2, attempt) * 1000; // 1s, 2s, 4s
        retryCountRef.current = attempt + 1;

        setReconnecting(true); // Set reconnecting state
        setTimeout(() => {
          setIsConnecting(false);
          // Trigger retry by calling connect again
          connect();
        }, delay);

        return; // Don't set connecting to false or show error yet
      }

      // Only show toast if it's not a token-related error (those are handled by auth system)
      if (!isAuthError) {
        toast.error('Verbindingsfout', 'Kon geen verbinding maken met chat server na meerdere pogingen');
      }

      setIsConnected(false);
      setConnectionStatus(false, error.message); // Update chat store with error
      retryCountRef.current = 0; // Reset retry count after max attempts
      setIsConnecting(false);
      setReconnecting(false);
    }
  }, [accessToken, token, isAuthenticated, user, isConnecting, isConnected, setConnectionStatus, setReconnecting]);

  const disconnect = useCallback(() => {
    socketService.disconnect();
    setIsConnected(false);
    setConnectionStatus(false, null); // Update chat store
  }, [setConnectionStatus]);

  // Stable identities (useCallback with only module-level refs): these are
  // used in consumers' effect dependency arrays (e.g. GroupChatPage's
  // join-effect), so a fresh identity per render would re-trigger those
  // effects on every provider render.
  const joinGroup = useCallback((groupId: number) => {
    try {
      socketService.joinGroup(groupId);
      // Fetch the initial online-users snapshot; subsequent changes arrive
      // via the 'online-users' broadcast.
      socketService.getOnlineUsers(groupId).then((users) => {
        useChatStore.getState().setOnlineUsers(
          groupId.toString(),
          users.map(transformBackendOnlineUser)
        );
      });
    } catch (error: any) {
      console.error('Failed to join group:', error);
      toast.error('Chat fout', 'Kon groep chat niet joinen');
    }
  }, []);

  const leaveGroup = useCallback((groupId: number) => {
    try {
      socketService.leaveGroup(groupId);
    } catch (error: any) {
      console.error('Failed to leave group:', error);
    }
  }, []);

  const sendMessage = useCallback(async (messageData: SendMessageData): Promise<ChatMessage | null> => {
    try {
      if (!socketService.isConnected()) {
        throw new Error('Socket not connected');
      }

      // Access the socket instance directly for enhanced message sending
      const socket = (socketService as any).socket;
      if (!socket?.connected) {
        throw new Error('Socket not connected');
      }

      // Emit the enhanced message data directly. The server ack (response.data)
      // carries the persisted message (real id/createdAt); the sender never gets
      // its own `new-message` broadcast, so this ack is the only way to
      // reconcile the optimistic message.
      return await new Promise<ChatMessage | null>((resolve, reject) => {
        let settled = false;

        socket.emit('send-message', messageData, (response: any) => {
          if (settled) return;
          settled = true;
          if (response?.success) {
            const realMessage = response.data
              ? groupMessageToChatMessage(response.data as GroupMessage)
              : null;
            resolve(realMessage);
          } else {
            // Server sends the human-readable reason under `error`.
            reject(new Error(response?.error || response?.message || 'Failed to send message'));
          }
        });

        // Add timeout in case server doesn't respond
        setTimeout(() => {
          if (settled) return;
          settled = true;
          reject(new Error('Message send timeout'));
        }, 10000);
      });
    } catch (error: any) {
      console.error('Failed to send message:', error);
      throw error; // Re-throw so MessageInput can handle it
    }
  }, []);

  const startTyping = useCallback((groupId: number) => {
    try {
      socketService.startTyping(groupId);
    } catch (error: any) {
      console.error('Failed to start typing indicator:', error);
    }
  }, []);

  const stopTyping = useCallback((groupId: number) => {
    try {
      socketService.stopTyping(groupId);
    } catch (error: any) {
      console.error('Failed to stop typing indicator:', error);
    }
  }, []);

  // Memoized so context consumers only re-render when something actually
  // changed, not on every provider render.
  const value: SocketContextType = useMemo(() => ({
    isConnected,
    isConnecting,
    connect,
    disconnect,
    joinGroup,
    leaveGroup,
    sendMessage,
    startTyping,
    stopTyping,
  }), [isConnected, isConnecting, connect, disconnect, joinGroup, leaveGroup, sendMessage, startTyping, stopTyping]);

  return (
    <SocketContext.Provider value={value}>
      {children}
    </SocketContext.Provider>
  );
};